// Model for UserInfo API response
export interface UserInfoModel {
  userName: string;
  userId: string;
  email: string;
  otpReqCountWithInThreasHold: number;
  validOtpReqCountWithInThreasHold: number;
  inValidOtpReqCountWithInThreasHold: number;
  productId: string | null;
}
// UI model for ResetPassword API
export interface ResetPasswordModel {
  userName: string;
  newPassword: string;
  otpCode: string;
  captchaCode: string;
  appName?: string;
}

import { Component, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppService } from '../app.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit {
    userInfo: UserInfoModel | null = null;
  loading: boolean = false;
  formHidden: boolean = false;
  otpVerified: boolean = false;

  showCaptcha: boolean = true;
  resetMessage: string = '';
  resetMessageColor: string = '';
  hidePassword = true;
  hideConfirmPassword = true;
  otpGeneratedAt: number | null = null;
  otpExpiryMinutes: number = environment.otpExpiryMinutes; // configurable from environment
  // Password rule checks for UI coloring
  get passwordValue(): string {
    return this.resetForm.get('password')?.value || '';
  }

  get passwordMinLength(): number {
    return environment.passwordMinLength;
  }

  isMinLength(): boolean {
    return this.passwordValue.length >= this.passwordMinLength;
  }
  isNoSpaces(): boolean {
    return !/\s/.test(this.passwordValue);
  }
  hasNumber(): boolean {
    return /[0-9]/.test(this.passwordValue);
  }
  hasUpper(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }
  hasSpecial(): boolean {
    return /[!@#$%^&*(),.?":{}|<>_\-+=~`\[\]\\/]/.test(this.passwordValue);
  }
  captchaCode: string = '';
  captchaInput: string = '';
  resetForm: FormGroup;
  otpSent = false;
  otpValue: string = '';
  userEnteredOtp: string = '';
  otpMessage: string = '';
  otpMessageColor: string = '';

  constructor(private fb: FormBuilder, private appService: AppService) {
    this.resetForm = this.fb.group({
      username: ['', [Validators.required]],
      captchaInput: ['', [Validators.required]],
      userEnteredOtp: ['', [Validators.required]],
      password: ['', [Validators.required, this.passwordComplexityValidator]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: [this.passwordsMatchValidator] });
  }

  passwordComplexityValidator = (control: import('@angular/forms').AbstractControl) => {
    const value = control.value || '';
    const minLength = value.length >= this.passwordMinLength;
    const noSpaces = !/\s/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasUpper = /[A-Z]/.test(value);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>_\-+=~`\[\]\\/]/.test(value);
    if (minLength && noSpaces && hasNumber && hasUpper && hasSpecial) {
      return null;
    }
    return { passwordInvalid: true };
  }

  passwordsMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  ngOnInit(): void {
    this.generateCaptcha();
  }

  generateCaptcha(): void {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    this.captchaCode = code;
    this.resetForm?.get('captchaInput')?.setValue('');
  }

  isCaptchaValid(): boolean {
    const input = this.resetForm?.get('captchaInput')?.value || '';
    return input.trim() === this.captchaCode;
  }

  // sendOtp() {
  //   // Generate a cryptographically secure 6-digit OTP
  //   const array = new Uint32Array(1);
  //   window.crypto.getRandomValues(array);
  //   const otp = (array[0] % 1000000).toString().padStart(6, '0');
  //   this.resetForm.get('otp')?.setValue(otp);
  //   // TODO: Integrate with backend to send OTP
  //   this.otpSent = true;
  //   this.otpGeneratedAt = Date.now();
  // }
  sendOtp() {
    // Generate a cryptographically secure 6-digit OTP
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    const otp = (array[0] % 1000000).toString().padStart(6, '0');
    this.otpValue = otp;
    //this.resetForm.get('otp')?.setValue(otp);
    //Validate captcha before sending OTP
    const username = this.resetForm.get('username')?.value;
    const captchaInput = this.resetForm.get('captchaInput')?.value || '';
    if (!username) {
      return;
    }
    if (!captchaInput || captchaInput.trim() !== this.captchaCode) {
      this.otpMessage = 'Invalid captcha. Please enter the correct captcha.';
      this.otpMessageColor = 'red';
      return;
    }
    this.otpSent = false;
    this.otpGeneratedAt = null;
    this.loading = true;
    this.appService.sendOtpToUser(username, otp, environment.productId).subscribe(
      (response: any) => {
        this.loading = false;
        if (response.success) {
          this.otpSent = true;
          this.otpGeneratedAt = Date.now();
          this.otpMessage = response.message || 'OTP email sent successfully.';
          this.otpMessageColor = 'green';
          this.showCaptcha = false;
          this.generateCaptcha();
          this.resetForm.get('captchaInput')?.setValue('');
          // Remove captchaInput required validator after OTP is sent
          this.resetForm.get('captchaInput')?.clearValidators();
          this.resetForm.get('captchaInput')?.updateValueAndValidity();
        } else {
          this.generateCaptcha();
          this.resetForm.get('captchaInput')?.setValue('');
          this.otpSent = false;
          this.otpMessage = response.message || 'Failed to send OTP email.';
          this.otpMessageColor = 'red';
        }
      },
      (err) => {
        this.loading = false;
        this.generateCaptcha();
        this.resetForm.get('captchaInput')?.setValue('');
        this.otpSent = false;
        this.otpMessage = 'Failed to send OTP email.';
        this.otpMessageColor = 'red';
      }
    );
  }
  // verifyOtp() {
  //       this.otpVerified = false;
  //       const userEnteredOtp = this.resetForm.get('userEnteredOtp')?.value || '';
  //       if (!userEnteredOtp) {
  //         this.otpMessage = 'Please enter a valid OTP.';
  //         this.otpMessageColor = 'red';
  //         return;
  //       }
  //       if (this.isOtpExpired()) {
  //         this.otpMessage = 'OTP expired. Please click Send OTP again.';
  //         this.otpMessageColor = 'red';
  //         return;
  //       }
  //       if (userEnteredOtp !== this.otpValue) {
  //         this.otpMessage = 'Please enter a valid OTP.';
  //         this.otpMessageColor = 'red';
  //         return;
  //       }
  //       this.otpVerified = true;
  //       this.otpMessage = 'OTP verified successfully!';
  //       this.otpMessageColor = 'green';         
  // }
  verifyOtp() {
    this.otpVerified = false;
    let userName = this.resetForm.get('username')?.value;
    const userEnteredOtp = this.resetForm.get('userEnteredOtp')?.value || '';

    
    this.appService.getUserInfo(userName).subscribe((response: any) => {
      if (response) {
        this.userInfo = {
          userName: response.userName,
          userId: response.userId,
          email: response.email,
          otpReqCountWithInThreasHold: response.otpReqCountWithInThreasHold,
          validOtpReqCountWithInThreasHold: response.validOtpReqCountWithInThreasHold,
          inValidOtpReqCountWithInThreasHold: response.inValidOtpReqCountWithInThreasHold,
          productId: response.productId
        };
        if (this.userInfo.inValidOtpReqCountWithInThreasHold >= 3 || this.userInfo.validOtpReqCountWithInThreasHold >= 3) {
          this.otpMessage = 'You have already tried validating the OTP 3 times. Please try after 30 minutes with a new OTP.';
          this.otpMessageColor = 'red';
        } else {
          if (!userEnteredOtp) {
            this.otpMessage = 'Please enter a valid OTP.';
            this.otpMessageColor = 'red';
            return;
          }
          if (this.isOtpExpired()) {
            this.otpMessage = 'OTP expired. Please click Send OTP again.';
            this.otpMessageColor = 'red';
            return;
          }
          if (userEnteredOtp !== this.otpValue) {
            this.otpMessage = 'Please enter a valid OTP.';
            this.otpMessageColor = 'red';
            this.appService.addUserOTPRequestInfo(this.userInfo.userId, environment.productId, "Invalid").subscribe((response: any) => {
            }, (error: any) => {
            });
            return;
          }
          this.otpVerified = true;
          this.otpMessage = 'OTP verified successfully!';
          this.otpMessageColor = 'green';
          this.appService.addUserOTPRequestInfo(this.userInfo.userId, environment.productId, "Valid").subscribe((response: any) => {
          }, (error: any) => {
          });
        }
      }
    }, (error: any) => {     
    });

   
  }

  isOtpExpired(): boolean {
    if (!this.otpGeneratedAt) return true;
    const now = Date.now();
    const expiryMs = this.otpExpiryMinutes * 60 * 1000;
    return now - this.otpGeneratedAt > expiryMs;
  }

  resetPassword() {
    // // Check if OTP was sent
    // if (!this.otpSent) {
    //   alert('Please send OTP before resetting your password.');
    //   return;
    // }
    // // Get user entered OTP
    this.otpMessage = '';
    const userEnteredOtp = this.resetForm.get('userEnteredOtp')?.value || '';
    // if (!userEnteredOtp) {
    //   this.otpMessage = 'Please enter a valid OTP.';
    //   return;
    // }
    // // Check if OTP is expired
    // if (this.isOtpExpired()) {
    //   this.otpMessage = 'OTP expired. Please click Send OTP again.';
    //   return;
    // }
    // // Check if user entered OTP matches the sent OTP
    // if (userEnteredOtp !== this.otpValue) {
    //   this.otpMessage = 'Please enter a valid OTP.';
    //   return;
    // }
    // Map form values to API model
    const model: ResetPasswordModel = {
      userName: this.resetForm.get('username')?.value,
      newPassword: this.resetForm.get('password')?.value,
      otpCode: userEnteredOtp,
      captchaCode: '', // Captcha is not validated here anymore
      appName: 'S3App' // or set dynamically if needed
    };
    if (this.resetForm.valid) {
      this.loading = true;
      this.appService.resetPasswordApi(model).subscribe(
        (response: any) => {
          this.loading = false;
          if (response.success) {
            this.otpMessage = '';
            this.resetMessage = 'Password reset successful! <a href="http://uatblacklight.s3partners.net/S3WebUat/Login.aspx" target="_blank" rel="noopener">Click here to login</a>.';
            this.resetMessageColor = 'green';
            this.formHidden = true;
            this.resetForm.reset();
            this.otpSent = false;
            this.otpGeneratedAt = null;
            this.otpVerified = false;
            this.showCaptcha = true;
            this.generateCaptcha();
          } else {
            this.otpMessage = '';
            this.resetMessage = 'Password reset failed.';
            this.resetMessageColor = 'red';
          }
        },
        (error) => {
          this.loading = false;
          this.otpMessage = '';
          this.resetMessage = 'Error occurred while resetting password.';
          this.resetMessageColor = 'red';
        }
      );
    } else if (this.resetForm.hasError('passwordMismatch')) {
      this.resetMessage = 'Passwords do not match!';
      this.resetMessageColor = 'red';
    } else if (this.resetForm.get('password')?.hasError('passwordInvalid')) {
      this.resetMessage = 'Password does not meet requirements!';
      this.resetMessageColor = 'red';
    }
  }
}
